import { DATETIME, MONTHS, formatValue } from './datePickerUtils';

/**
 * Section-by-section typing for shared/DatePicker (IGFEMS-673). The field is always whole, like a
 * native date input: typing fills the active section (day, then month, then year) and the others
 * keep their value or placeholder. Every function here returns the next edit state, or the same
 * object when a key is refused.
 */
const SECTIONS = [
  { kind: 'day', placeholder: 'dd', separator: '-', max: 31 },
  { kind: 'month', placeholder: 'MMM', separator: '-' },
  { kind: 'year', placeholder: 'yyyy', separator: ' ' },
  { kind: 'hour', placeholder: 'HH', separator: ':', max: 23 },
  { kind: 'minute', placeholder: 'mm', separator: '', max: 59 },
];

const sectionsOf = (edit) => SECTIONS.slice(0, edit.parts.length);
const lastIndex = (edit) => edit.parts.length - 1;

export const startEdit = (value, variant) => {
  const count = variant === DATETIME ? 5 : 3;
  const text = formatValue(value);
  return {
    parts: text ? text.split(/[- :]/) : Array(count).fill(''),
    active: 0,
    fresh: true,
    dirty: false,
    autoAdvanced: false,
  };
};

const shownParts = (edit) => edit.parts.map((part, i) => part || SECTIONS[i].placeholder);

export const editText = (edit) =>
  shownParts(edit).reduce((text, part, i) => `${text}${part}${i < lastIndex(edit) ? SECTIONS[i].separator : ''}`, '');

export const sectionRange = (edit, index) => {
  const start = shownParts(edit)
    .slice(0, index)
    .reduce((total, part, i) => total + part.length + SECTIONS[i].separator.length, 0);
  return [start, start + shownParts(edit)[index].length];
};

export const sectionAt = (edit, caret) => {
  for (let i = 0; i < lastIndex(edit); i += 1) {
    if (caret <= sectionRange(edit, i)[1]) return i;
  }
  return lastIndex(edit);
};

export const isEmptyEdit = (edit) => edit.parts.every((part) => !part);

/** A section left part-typed is finished off: "5" becomes "05", and an unfinished month goes back to empty. */
const finishActive = (edit) => {
  const { kind, max } = SECTIONS[edit.active];
  const part = edit.parts[edit.active];
  if (!part) return edit;
  if (kind === 'month' && !MONTHS.includes(part)) return replaceActive(edit, '');
  if (max && part.length === 1) return replaceActive(edit, `0${part}`);
  return edit;
};

function replaceActive(edit, part) {
  const parts = [...edit.parts];
  parts[edit.active] = part;
  return { ...edit, parts };
}

/** Typed text for parseTyped. A datetime with no time typed at all is left date-only. */
export const editToTyped = (edit) => {
  const { parts } = finishActive(edit);
  const date = `${parts[0]}-${parts[1]}-${parts[2]}`;
  return parts[3] || parts[4] ? `${date} ${parts[3]}:${parts[4]}` : date;
};

export const moveTo = (edit, index) => {
  const finished = finishActive(edit);
  return { ...finished, active: Math.max(0, Math.min(index, lastIndex(edit))), fresh: true, autoAdvanced: false };
};

const filled = (edit, part) => {
  const next = { ...replaceActive(edit, part), dirty: true, fresh: true };
  if (edit.active === lastIndex(edit)) return { ...next, autoAdvanced: false };
  return { ...next, active: edit.active + 1, autoAdvanced: true };
};

const inProgress = (edit, part) => ({ ...replaceActive(edit, part), dirty: true, fresh: false, autoAdvanced: false });

const typeMonth = (edit, letter) => {
  const typed = `${edit.fresh ? '' : edit.parts[edit.active]}${letter}`;
  const shaped = typed[0].toUpperCase() + typed.slice(1).toLowerCase();
  const matches = MONTHS.filter((month) => month.startsWith(shaped));
  if (!matches.length) return edit;
  return matches.length === 1 ? filled(edit, matches[0]) : inProgress(edit, shaped);
};

const typeNumber = (edit, digit) => {
  const { kind, max } = SECTIONS[edit.active];
  const typed = `${edit.fresh ? '' : edit.parts[edit.active]}${digit}`;
  if (kind === 'year') return typed.length === 4 ? filled(edit, typed) : inProgress(edit, typed);
  if (typed.length === 1) {
    // No second digit could make this real, so "5" in the day is already 05.
    return Number(digit) * 10 > max ? filled(edit, `0${digit}`) : inProgress(edit, typed);
  }
  const tooLow = kind === 'day' && typed === '00';
  return Number(typed) > max || tooLow ? edit : filled(edit, typed);
};

export const typeKey = (edit, key) => {
  if (/^\d$/.test(key)) return SECTIONS[edit.active].kind === 'month' ? edit : typeNumber(edit, key);
  if (/^[a-z]$/i.test(key)) return SECTIONS[edit.active].kind === 'month' ? typeMonth(edit, key) : edit;
  if (/^[-/.: ]$/.test(key)) {
    // After "10" the month is already active, so the "-" in "10-Oct" must not skip past it.
    if (edit.autoAdvanced) return { ...edit, autoAdvanced: false };
    return moveTo(edit, edit.active + 1);
  }
  return edit;
};

export const eraseActive = (edit) => {
  if (!edit.parts[edit.active]) return edit;
  return { ...replaceActive(edit, ''), dirty: true, fresh: true, autoAdvanced: false };
};

/** Backspace empties the active section, then steps back to the one before. */
export const backspace = (edit) => (edit.parts[edit.active] ? eraseActive(edit) : moveTo(edit, edit.active - 1));

export const clearAll = (edit) => ({
  parts: edit.parts.map(() => ''),
  active: 0,
  fresh: true,
  dirty: true,
  autoAdvanced: false,
});
